// What Kòb pa m says: a conversion as one spoken sentence, in any of Palefò's
// eleven voice languages, with every number written out in words.
//
// In words because the voices need it: Palefò's multilingual voice reads the
// digits "65435" one at a time. And an .mjs because this is the only file
// loaded by BOTH the browser bundle and the Node API — .mjs is the one spelling
// webpack, Jest and every Node version agree is an ES module. The page shows
// the sentence; api/tts.js composes the very same one from the numbers it is
// sent, so the server never voices text a browser wrote.

const MAX_SPOKEN = 1e12;   // every language here covers 0 … 999,999,999,999

// The count of each scale in n: [billions, millions, thousands, the rest under a thousand].
const groups = (n) => [Math.floor(n / 1e9), Math.floor(n / 1e6) % 1000, Math.floor(n / 1e3) % 1000, n % 1000];

// ── Kreyòl ── number words 0–99 follow pratik/js/game/numbers.js, so the
// Palefò family's apps say a number the same way.

const HT_UNITS = ['zewo', 'en', 'de', 'twa', 'kat', 'senk', 'sis', 'sèt', 'uit', 'nèf'];
const HT_TEENS = ['dis', 'onz', 'douz', 'trèz', 'katòz', 'kenz', 'sèz', 'disèt', 'dizuit', 'diznèf'];
const HT_TENS = { 20: 'ven', 30: 'trant', 40: 'karant', 50: 'senkant', 60: 'swasant' };

export function kreyolUnder100(n) {
  if (n < 10) return HT_UNITS[n];
  if (n < 20) return HT_TEENS[n - 10];
  if (n < 70) {
    const tens = Math.floor(n / 10) * 10, u = n % 10, base = HT_TENS[tens];
    if (u === 0) return base;
    const stem = base.endsWith('t') ? base.slice(0, -1) : base;     // ven, tran, karan…
    if (u === 1) return stem + 'teyen';                             // venteyen, tranteyen
    if (u <= 7) return stem + 'n' + HT_UNITS[u];                    // vennde, trannde, karannde
    return stem + 't' + HT_UNITS[u];                                // ventuit, ventnèf, trantuit
  }
  if (n < 80) return n === 70 ? 'swasanndis' : 'swasann' + HT_TEENS[n - 70];
  if (n < 90) return n === 80 ? 'katreven' : 'katreven' + HT_UNITS[n - 80];
  return n === 90 ? 'katrevendis' : 'katreven' + HT_TEENS[n - 90];
}

function kreyolNumber(n) {
  if (n < 100) return kreyolUnder100(n);
  const parts = [];
  groups(n).slice(0, 3).forEach((count, i) => {
    const scale = ['milya', 'milyon', 'mil'][i];
    if (count === 1) parts.push(scale === 'mil' ? 'mil' : `yon ${scale}`);
    else if (count) parts.push(`${kreyolNumber(count)} ${scale}`);
  });
  const rest = n % 1000;
  if (rest >= 100) parts.push(rest < 200 ? 'san' : `${HT_UNITS[Math.floor(rest / 100)]} san`);
  if (rest % 100) parts.push(kreyolUnder100(rest % 100));
  return parts.join(' ');
}

// ── English ──

const EN_ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven',
  'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const EN_TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function englishUnder1000(n) {
  const parts = [], rest = n % 100;
  if (n >= 100) parts.push(`${EN_ONES[Math.floor(n / 100)]} hundred`);
  if (rest >= 20) parts.push(EN_TENS[Math.floor(rest / 10)] + (rest % 10 ? `-${EN_ONES[rest % 10]}` : ''));
  else if (rest || !n) parts.push(EN_ONES[rest]);
  return parts.join(' ');
}

function englishNumber(n) {
  if (n < 1000) return englishUnder1000(n);
  return groups(n)
    .map((count, i) => (count ? `${englishUnder1000(count)} ${['billion', 'million', 'thousand', ''][i]}`.trim() : ''))
    .filter(Boolean).join(' ');
}

// ── French ──

const FR_ONES = ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix', 'onze',
  'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
const FR_TENS = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante'];

function frenchUnder100(n) {
  if (n < 20) return FR_ONES[n];
  const tens = Math.floor(n / 10), u = n % 10;
  if (tens === 8) return u ? `quatre-vingt-${FR_ONES[u]}` : 'quatre-vingts';
  if (tens === 9) return `quatre-vingt-${FR_ONES[10 + u]}`;                              // quatre-vingt-dix-sept
  if (tens === 7) return u === 1 ? 'soixante et onze' : `soixante-${FR_ONES[10 + u]}`;   // soixante-douze
  if (u === 1) return `${FR_TENS[tens]} et un`;
  return FR_TENS[tens] + (u ? `-${FR_ONES[u]}` : '');
}

// `last` is false only in front of "mille", where "cents" and "quatre-vingts"
// lose their s: deux cents, deux cent mille; quatre-vingts, quatre-vingt mille.
function frenchUnder1000(n, last) {
  const parts = [], h = Math.floor(n / 100), rest = n % 100;
  if (h) parts.push(h === 1 ? 'cent' : `${FR_ONES[h]} cent${rest || !last ? '' : 's'}`);
  if (rest || !n) parts.push(rest === 80 && !last ? 'quatre-vingt' : frenchUnder100(rest));
  return parts.join(' ');
}

function frenchNumber(n) {
  const [billions, millions, thousands, rest] = groups(n);
  const parts = [];
  if (billions) parts.push(`${frenchUnder1000(billions, true)} milliard${billions > 1 ? 's' : ''}`);
  if (millions) parts.push(`${frenchUnder1000(millions, true)} million${millions > 1 ? 's' : ''}`);
  if (thousands) parts.push(thousands === 1 ? 'mille' : `${frenchUnder1000(thousands, false)} mille`);
  if (rest || !n) parts.push(frenchUnder1000(rest, true));
  return parts.join(' ');
}

// ── Spanish ──

const ES_ONES = ['cero', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve', 'diez', 'once',
  'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve', 'veinte', 'veintiuno',
  'veintidós', 'veintitrés', 'veinticuatro', 'veinticinco', 'veintiséis', 'veintisiete', 'veintiocho', 'veintinueve'];
const ES_TENS = ['', '', '', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
const ES_HUNDREDS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos',
  'setecientos', 'ochocientos', 'novecientos'];

function spanishUnder1000(n) {
  if (n === 100) return 'cien';
  const parts = [], rest = n % 100;
  if (n > 100) parts.push(ES_HUNDREDS[Math.floor(n / 100)]);
  if (rest >= 30) parts.push(ES_TENS[Math.floor(rest / 10)] + (rest % 10 ? ` y ${ES_ONES[rest % 10]}` : ''));
  else if (rest || !n) parts.push(ES_ONES[rest]);
  return parts.join(' ');
}

// "uno" shortens in front of a noun or a scale word: un dólar, veintiún mil, treinta y un millones.
const spanishShort = (words) => words.replace(/veintiuno$/, 'veintiún').replace(/uno$/, 'un');

// A billion is "mil millones", so the millions run to 999,999 of them.
function spanishNumber(n) {
  const millions = Math.floor(n / 1e6), thousands = Math.floor(n / 1e3) % 1000, rest = n % 1000;
  const parts = [];
  if (millions) parts.push(millions === 1 ? 'un millón' : `${spanishShort(spanishNumber(millions))} millones`);
  if (thousands) parts.push(thousands === 1 ? 'mil' : `${spanishShort(spanishUnder1000(thousands))} mil`);
  if (rest || !n) parts.push(spanishUnder1000(rest));
  return parts.join(' ');
}

// ── Portuguese (Brazilian) ──

const PT_ONES = ['zero', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove', 'dez', 'onze',
  'doze', 'treze', 'catorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
const PT_TENS = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
const PT_HUNDREDS = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos',
  'setecentos', 'oitocentos', 'novecentos'];

function portugueseUnder1000(n) {
  if (n === 100) return 'cem';
  const parts = [], rest = n % 100;
  if (n > 100) parts.push(PT_HUNDREDS[Math.floor(n / 100)]);
  if (rest >= 20) parts.push(PT_TENS[Math.floor(rest / 10)] + (rest % 10 ? ` e ${PT_ONES[rest % 10]}` : ''));
  else if (rest || !n) parts.push(PT_ONES[rest]);
  return parts.join(' e ');                         // cento e um, duzentos e trinta e cinco
}

function portugueseNumber(n) {
  const [billions, millions, thousands, rest] = groups(n);
  const parts = [];   // [value of the group, its words]
  if (billions) parts.push([billions, billions === 1 ? 'um bilhão' : `${portugueseUnder1000(billions)} bilhões`]);
  if (millions) parts.push([millions, millions === 1 ? 'um milhão' : `${portugueseUnder1000(millions)} milhões`]);
  if (thousands) parts.push([thousands, thousands === 1 ? 'mil' : `${portugueseUnder1000(thousands)} mil`]);
  if (rest || !n) parts.push([rest, portugueseUnder1000(rest)]);
  // "e" joins the last group when it is under a hundred or a round hundred: mil e quinhentos, mil duzentos e trinta.
  return parts.map(([value, words], i) => (
    i && i === parts.length - 1 && (value < 100 || value % 100 === 0) ? `e ${words}` : words
  )).join(' ');
}

// ── Italian and German ── both write a number as one word up to a million
// (sessantacinquemilaquattrocentotrentacinque). Here it gets a space after the
// hundreds and after the thousands instead: Palefò's voice gives up on a word
// of thirty letters, and reads the pieces exactly as it would the whole.

const IT_ONES = ['zero', 'uno', 'due', 'tre', 'quattro', 'cinque', 'sei', 'sette', 'otto', 'nove', 'dieci', 'undici',
  'dodici', 'tredici', 'quattordici', 'quindici', 'sedici', 'diciassette', 'diciotto', 'diciannove'];
const IT_TENS = ['', '', 'venti', 'trenta', 'quaranta', 'cinquanta', 'sessanta', 'settanta', 'ottanta', 'novanta'];

function italianUnder100(n) {
  if (n < 20) return IT_ONES[n];
  const tens = IT_TENS[Math.floor(n / 10)], u = n % 10;
  if (u === 1 || u === 8) return tens.slice(0, -1) + IT_ONES[u];   // ventuno, ventotto
  return tens + (u ? IT_ONES[u] : '');
}
// [the hundreds, the rest]: 435 → ['quattrocento', 'trentacinque'].
const italianUnder1000 = (n) => [
  n >= 100 ? (n < 200 ? '' : IT_ONES[Math.floor(n / 100)]) + 'cento' : '',
  n % 100 || !n ? italianUnder100(n % 100) : '',
].filter(Boolean);

function italianNumber(n) {
  const [billions, millions, thousands, rest] = groups(n);
  const parts = [];
  if (billions) parts.push(billions === 1 ? 'un miliardo' : `${italianUnder1000(billions).join(' ')} miliardi`);
  if (millions) parts.push(millions === 1 ? 'un milione' : `${italianUnder1000(millions).join(' ')} milioni`);
  if (thousands) parts.push(thousands === 1 ? 'mille' : `${italianUnder1000(thousands).join(' ')}mila`);
  if (rest || !n) parts.push(...italianUnder1000(rest));
  return parts.join(' ').replace(/([a-z])tre$/, '$1tré');   // ventitré, trentatré — but tre on its own
}

const DE_ONES = ['null', 'eins', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun', 'zehn', 'elf',
  'zwölf', 'dreizehn', 'vierzehn', 'fünfzehn', 'sechzehn', 'siebzehn', 'achtzehn', 'neunzehn'];
const DE_TENS = ['', '', 'zwanzig', 'dreißig', 'vierzig', 'fünfzig', 'sechzig', 'siebzig', 'achtzig', 'neunzig'];

// "eins" only where the number ends; inside a compound it is "ein": einundzwanzig, eintausend.
function germanUnder100(n, ends) {
  if (n === 1) return ends ? 'eins' : 'ein';
  if (n < 20) return DE_ONES[n];
  const u = n % 10;
  return (u ? (u === 1 ? 'ein' : DE_ONES[u]) + 'und' : '') + DE_TENS[Math.floor(n / 10)];
}
// [the hundreds, the rest]: 435 → ['vierhundert', 'fünfunddreißig'].
const germanUnder1000 = (n, ends) => [
  n >= 100 ? (n < 200 ? 'ein' : DE_ONES[Math.floor(n / 100)]) + 'hundert' : '',
  n % 100 || !n ? germanUnder100(n % 100, ends) : '',
].filter(Boolean);

function germanNumber(n) {
  const [billions, millions, thousands, rest] = groups(n);
  const parts = [];
  if (billions) parts.push(billions === 1 ? 'eine Milliarde' : `${germanUnder1000(billions, true).join(' ')} Milliarden`);
  if (millions) parts.push(millions === 1 ? 'eine Million' : `${germanUnder1000(millions, true).join(' ')} Millionen`);
  if (thousands) parts.push(`${germanUnder1000(thousands, false).join(' ')}tausend`);
  if (rest || !n) parts.push(...germanUnder1000(rest, true));
  return parts.join(' ');
}

// ── Russian ── numerals agree in gender (одна тысяча, две тысячи), and a noun
// takes one of three forms after a number: 1 гурд, 2 гурда, 5 гурдов.

const RU_ONES = ['ноль', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять', 'десять',
  'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать', 'пятнадцать', 'шестнадцать', 'семнадцать',
  'восемнадцать', 'девятнадцать'];
const RU_TENS = ['', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят', 'восемьдесят', 'девяносто'];
const RU_HUNDREDS = ['', 'сто', 'двести', 'триста', 'четыреста', 'пятьсот', 'шестьсот', 'семьсот', 'восемьсот', 'девятьсот'];

const russianForm = (n) => {
  const tens = n % 100, u = n % 10;
  if (tens >= 11 && tens <= 14) return 2;
  return u === 1 ? 0 : u >= 2 && u <= 4 ? 1 : 2;
};

function russianUnder1000(n, feminine) {
  const parts = [], rest = n % 100, u = rest < 20 ? rest : rest % 10;
  if (n >= 100) parts.push(RU_HUNDREDS[Math.floor(n / 100)]);
  if (rest >= 20) parts.push(RU_TENS[Math.floor(rest / 10)]);
  if (u || !n) parts.push(feminine && u === 1 ? 'одна' : feminine && u === 2 ? 'две' : RU_ONES[u]);
  return parts.join(' ');
}

function russianNumber(n) {
  const [billions, millions, thousands, rest] = groups(n);
  const parts = [];
  if (billions) parts.push(`${russianUnder1000(billions)} ${['миллиард', 'миллиарда', 'миллиардов'][russianForm(billions)]}`);
  if (millions) parts.push(`${russianUnder1000(millions)} ${['миллион', 'миллиона', 'миллионов'][russianForm(millions)]}`);
  if (thousands) parts.push(`${russianUnder1000(thousands, true)} ${['тысяча', 'тысячи', 'тысяч'][russianForm(thousands)]}`);
  if (rest || !n) parts.push(russianUnder1000(rest));
  return parts.join(' ');
}

// ── Chinese, Japanese, Korean ── counted in myriads (万 / 만 = 10,000; 亿 / 억 = 100,000,000).

const myriads = (n) => [Math.floor(n / 1e8), Math.floor(n / 1e4) % 1e4, n % 1e4];
const PLACES = [1000, 100, 10, 1];

const ZH_DIGITS = '零一二三四五六七八九';
function chineseUnder10000(n) {
  let words = '', gap = false;
  PLACES.forEach((place, i) => {
    const d = Math.floor(n / place) % 10;
    if (!d) { gap = words !== ''; return; }
    words += (gap ? '零' : '') + ZH_DIGITS[d] + '千百十'.charAt(i);   // a skipped place is said: 一千零五
    gap = false;
  });
  return words;
}
function chineseNumber(n) {
  if (!n) return '零';
  let words = '';
  myriads(n).forEach((count, i) => {
    if (count) words += (words && count < 1000 ? '零' : '') + chineseUnder10000(count) + '亿万'.charAt(i);
  });
  return words.replace(/^一十/, '十');                // 十二, not 一十二
}

const JA_DIGITS = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
// 百 and 千 stand alone for one of them — except 一千 in front of 万 and 億.
function japaneseUnder10000(n, scaled) {
  return PLACES.map((place, i) => {
    const d = Math.floor(n / place) % 10, unit = '千百十'.charAt(i);
    if (!d) return '';
    return (d === 1 && unit && !(scaled && unit === '千') ? '' : JA_DIGITS[d]) + unit;
  }).join('');
}
function japaneseNumber(n) {
  if (!n) return 'ゼロ';
  return myriads(n).map((count, i) => (count ? japaneseUnder10000(count, i < 2) + '億万'.charAt(i) : '')).join('');
}

const KO_DIGITS = ['', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];
function koreanUnder10000(n) {
  return PLACES.map((place, i) => {
    const d = Math.floor(n / place) % 10, unit = '천백십'.charAt(i);
    return d ? (d === 1 && unit ? '' : KO_DIGITS[d]) + unit : '';
  }).join('');
}
function koreanNumber(n) {
  if (!n) return '영';
  const [eok, man, rest] = myriads(n);
  return [eok && `${koreanUnder10000(eok)}억`, man && (man === 1 ? '만' : `${koreanUnder10000(man)}만`),
    rest && koreanUnder10000(rest)].filter(Boolean).join(' ');
}
// 은 after a final consonant, 는 after a vowel: 달러는, 상팀은.
const koreanTopic = (word) => ((word.charCodeAt(word.length - 1) - 0xac00) % 28 ? '은' : '는');

// ── the languages ──
//
// For each: `number(n)` on its own, `count(n, currency)` as it stands in front
// of a noun, `form(n)` picking which of the noun's forms follows that number,
// and the three nouns per currency — `unit`, `cents` after a whole amount,
// `centsAlone` with none. The rest is how a sentence is put together.

// A language written with spaces between its words.
const spaced = ({ and, or, makes }) => ({
  amount: (count, noun) => `${count} ${noun}`,
  withCents: (whole, cents) => `${whole} ${and} ${cents}`,
  sentence: (source, targets, amount) => `${source} ${makes(amount)} ${targets.join(`, ${or} `)}.`,
});
const singularAtOne = (n) => (n === 1 ? 0 : 1);
const exactMillions = (n) => n >= 1e6 && n % 1e6 === 0;   // "deux millions DE gourdes"

const LANGUAGES = {
  ht: {
    number: kreyolNumber,
    count: (n) => (n === 1 ? 'yon' : kreyolNumber(n)),
    form: () => 0,
    unit: { usd: ['dola ameriken'], htg: ['goud'], htd: ['dola ayisyen'], eur: ['ewo'], cad: ['dola kanadyen'], dop: ['peso dominiken'] },
    cents: { usd: ['santim'], htg: ['santim'], htd: ['santim'], eur: ['santim'], cad: ['santim'], dop: ['santim'] },
    centsAlone: {
      usd: ['santim ameriken'], htg: ['santim'], htd: ['santim ayisyen'],
      eur: ['santim ewo'], cad: ['santim kanadyen'], dop: ['santim dominiken'],
    },
    ...spaced({ and: 'ak', or: 'oswa', makes: () => 'fè' }),
  },
  en: {
    number: englishNumber,
    count: englishNumber,
    form: singularAtOne,
    unit: {
      usd: ['US dollar', 'US dollars'], htg: ['gourde', 'gourdes'], htd: ['Haitian dollar', 'Haitian dollars'],
      eur: ['euro', 'euros'], cad: ['Canadian dollar', 'Canadian dollars'], dop: ['Dominican peso', 'Dominican pesos'],
    },
    cents: {
      usd: ['cent', 'cents'], htg: ['centime', 'centimes'], htd: ['cent', 'cents'],
      eur: ['cent', 'cents'], cad: ['cent', 'cents'], dop: ['centavo', 'centavos'],
    },
    centsAlone: {
      usd: ['US cent', 'US cents'], htg: ['centime', 'centimes'], htd: ['Haitian cent', 'Haitian cents'],
      eur: ['euro cent', 'euro cents'], cad: ['Canadian cent', 'Canadian cents'], dop: ['Dominican centavo', 'Dominican centavos'],
    },
    ...spaced({ and: 'and', or: 'or', makes: () => 'is' }),
  },
  fr: {
    number: frenchNumber,
    // "une gourde", "vingt et une gourdes"; "deux millions de gourdes".
    count: (n, currency) => frenchNumber(n).replace(/\bun$/, currency === 'htg' ? 'une' : 'un') + (exactMillions(n) ? ' de' : ''),
    form: (n) => (n < 2 ? 0 : 1),
    unit: {
      usd: ['dollar américain', 'dollars américains'], htg: ['gourde', 'gourdes'], htd: ['dollar haïtien', 'dollars haïtiens'],
      eur: ['euro', 'euros'], cad: ['dollar canadien', 'dollars canadiens'], dop: ['peso dominicain', 'pesos dominicains'],
    },
    cents: {
      usd: ['centime', 'centimes'], htg: ['centime', 'centimes'], htd: ['centime', 'centimes'],
      eur: ['centime', 'centimes'], cad: ['centime', 'centimes'], dop: ['centavo', 'centavos'],
    },
    centsAlone: {
      usd: ['centime américain', 'centimes américains'], htg: ['centime de gourde', 'centimes de gourde'],
      htd: ['centime haïtien', 'centimes haïtiens'], eur: ["centime d'euro", "centimes d'euro"],
      cad: ['centime canadien', 'centimes canadiens'], dop: ['centavo dominicain', 'centavos dominicains'],
    },
    ...spaced({ and: 'et', or: 'ou', makes: (amount) => (amount < 2 ? 'fait' : 'font') }),
  },
  es: {
    number: spanishNumber,
    count: (n) => spanishShort(spanishNumber(n)) + (exactMillions(n) ? ' de' : ''),
    form: singularAtOne,
    unit: {
      usd: ['dólar estadounidense', 'dólares estadounidenses'], htg: ['gourde', 'gourdes'], htd: ['dólar haitiano', 'dólares haitianos'],
      eur: ['euro', 'euros'], cad: ['dólar canadiense', 'dólares canadienses'], dop: ['peso dominicano', 'pesos dominicanos'],
    },
    cents: {
      usd: ['centavo', 'centavos'], htg: ['céntimo', 'céntimos'], htd: ['centavo', 'centavos'],
      eur: ['céntimo', 'céntimos'], cad: ['centavo', 'centavos'], dop: ['centavo', 'centavos'],
    },
    centsAlone: {
      usd: ['centavo de dólar estadounidense', 'centavos de dólar estadounidense'],
      htg: ['céntimo de gourde', 'céntimos de gourde'], htd: ['centavo de dólar haitiano', 'centavos de dólar haitiano'],
      eur: ['céntimo de euro', 'céntimos de euro'], cad: ['centavo de dólar canadiense', 'centavos de dólar canadiense'],
      dop: ['centavo de peso dominicano', 'centavos de peso dominicano'],
    },
    ...spaced({ and: 'con', or: 'o', makes: (amount) => (amount === 1 ? 'equivale a' : 'equivalen a') }),
  },
  pt: {
    number: portugueseNumber,
    count: (n) => portugueseNumber(n) + (exactMillions(n) ? ' de' : ''),
    form: singularAtOne,
    unit: {
      usd: ['dólar americano', 'dólares americanos'], htg: ['gourde', 'gourdes'], htd: ['dólar haitiano', 'dólares haitianos'],
      eur: ['euro', 'euros'], cad: ['dólar canadense', 'dólares canadenses'], dop: ['peso dominicano', 'pesos dominicanos'],
    },
    cents: {
      usd: ['centavo', 'centavos'], htg: ['centavo', 'centavos'], htd: ['centavo', 'centavos'],
      eur: ['cêntimo', 'cêntimos'], cad: ['centavo', 'centavos'], dop: ['centavo', 'centavos'],
    },
    centsAlone: {
      usd: ['centavo de dólar americano', 'centavos de dólar americano'],
      htg: ['centavo de gourde', 'centavos de gourde'], htd: ['centavo de dólar haitiano', 'centavos de dólar haitiano'],
      eur: ['cêntimo de euro', 'cêntimos de euro'], cad: ['centavo de dólar canadense', 'centavos de dólar canadense'],
      dop: ['centavo de peso dominicano', 'centavos de peso dominicano'],
    },
    ...spaced({ and: 'e', or: 'ou', makes: (amount) => (amount === 1 ? 'equivale a' : 'equivalem a') }),
  },
  it: {
    number: italianNumber,
    count: (n) => (n === 1 ? 'un' : italianNumber(n) + (exactMillions(n) ? ' di' : '')),
    form: singularAtOne,
    unit: {
      usd: ['dollaro statunitense', 'dollari statunitensi'], htg: ['gourde', 'gourde'], htd: ['dollaro haitiano', 'dollari haitiani'],
      eur: ['euro', 'euro'], cad: ['dollaro canadese', 'dollari canadesi'], dop: ['peso dominicano', 'pesos dominicani'],
    },
    cents: {
      usd: ['centesimo', 'centesimi'], htg: ['centesimo', 'centesimi'], htd: ['centesimo', 'centesimi'],
      eur: ['centesimo', 'centesimi'], cad: ['centesimo', 'centesimi'], dop: ['centavo', 'centavos'],
    },
    centsAlone: {
      usd: ['centesimo di dollaro statunitense', 'centesimi di dollaro statunitense'],
      htg: ['centesimo di gourde', 'centesimi di gourde'], htd: ['centesimo di dollaro haitiano', 'centesimi di dollaro haitiano'],
      eur: ['centesimo di euro', 'centesimi di euro'], cad: ['centesimo di dollaro canadese', 'centesimi di dollaro canadese'],
      dop: ['centavo di peso dominicano', 'centavos di peso dominicano'],
    },
    ...spaced({ and: 'e', or: 'o', makes: (amount) => (amount === 1 ? 'equivale a' : 'equivalgono a') }),
  },
  de: {
    number: germanNumber,
    count: (n, currency) => (n === 1 ? (currency === 'htg' ? 'eine' : 'ein') : germanNumber(n)),   // die Gourde, der Dollar
    form: singularAtOne,
    unit: {
      usd: ['US-Dollar', 'US-Dollar'], htg: ['Gourde', 'Gourdes'], htd: ['haitianischer Dollar', 'haitianische Dollar'],
      eur: ['Euro', 'Euro'], cad: ['kanadischer Dollar', 'kanadische Dollar'], dop: ['dominikanischer Peso', 'dominikanische Pesos'],
    },
    cents: {
      usd: ['Cent', 'Cent'], htg: ['Centime', 'Centimes'], htd: ['Cent', 'Cent'],
      eur: ['Cent', 'Cent'], cad: ['Cent', 'Cent'], dop: ['Centavo', 'Centavos'],
    },
    centsAlone: {
      usd: ['US-Cent', 'US-Cent'], htg: ['Centime', 'Centimes'], htd: ['haitianischer Cent', 'haitianische Cent'],
      eur: ['Eurocent', 'Eurocent'], cad: ['kanadischer Cent', 'kanadische Cent'], dop: ['dominikanischer Centavo', 'dominikanische Centavos'],
    },
    ...spaced({ and: 'und', or: 'oder', makes: (amount) => (amount === 1 ? 'ist' : 'sind') }),
  },
  ru: {
    number: russianNumber,
    // The nouns below are masculine, except песо (neuter, undeclined): одно песо.
    count: (n, currency) => (currency === 'dop' ? russianNumber(n).replace(/один$/, 'одно') : russianNumber(n)),
    form: russianForm,
    unit: {
      usd: ['доллар США', 'доллара США', 'долларов США'], htg: ['гурд', 'гурда', 'гурдов'],
      htd: ['гаитянский доллар', 'гаитянских доллара', 'гаитянских долларов'],
      eur: ['евро', 'евро', 'евро'], cad: ['канадский доллар', 'канадских доллара', 'канадских долларов'],
      dop: ['доминиканское песо', 'доминиканских песо', 'доминиканских песо'],
    },
    cents: {
      usd: ['цент', 'цента', 'центов'], htg: ['сантим', 'сантима', 'сантимов'], htd: ['цент', 'цента', 'центов'],
      eur: ['цент', 'цента', 'центов'], cad: ['цент', 'цента', 'центов'], dop: ['сентаво', 'сентаво', 'сентаво'],
    },
    centsAlone: {
      usd: ['цент США', 'цента США', 'центов США'], htg: ['сантим', 'сантима', 'сантимов'],
      htd: ['гаитянский цент', 'гаитянских цента', 'гаитянских центов'],
      eur: ['евроцент', 'евроцента', 'евроцентов'], cad: ['канадский цент', 'канадских цента', 'канадских центов'],
      dop: ['доминиканское сентаво', 'доминиканских сентаво', 'доминиканских сентаво'],
    },
    ...spaced({ and: 'и', or: 'или', makes: () => '— это' }),
  },
  zh: {
    number: chineseNumber,
    count: (n) => (n === 2 ? '两' : chineseNumber(n)),                  // 两美元
    form: () => 0,
    unit: { usd: ['美元'], htg: ['古德'], htd: ['海地元'], eur: ['欧元'], cad: ['加拿大元'], dop: ['多米尼加比索'] },
    cents: { usd: ['美分'], htg: ['生丁'], htd: ['分'], eur: ['欧分'], cad: ['分'], dop: ['分'] },
    centsAlone: { usd: ['美分'], htg: ['生丁'], htd: ['海地分'], eur: ['欧分'], cad: ['加拿大分'], dop: ['多米尼加分'] },
    amount: (count, noun) => count + noun,
    withCents: (whole, cents) => whole + cents,
      sentence: (source, targets) => `${source}等于${targets.join('，或')}。`,
  },
  ja: {
    number: japaneseNumber,
    count: japaneseNumber,
    form: () => 0,
    unit: { usd: ['米ドル'], htg: ['グールド'], htd: ['ハイチドル'], eur: ['ユーロ'], cad: ['カナダドル'], dop: ['ドミニカペソ'] },
    cents: { usd: ['セント'], htg: ['サンチーム'], htd: ['セント'], eur: ['セント'], cad: ['セント'], dop: ['センターボ'] },
    centsAlone: { usd: ['米セント'], htg: ['サンチーム'], htd: ['ハイチセント'], eur: ['ユーロセント'], cad: ['カナダセント'], dop: ['ドミニカセンターボ'] },
    amount: (count, noun) => count + noun,
    withCents: (whole, cents) => whole + cents,
      sentence: (source, targets) => `${source}は${targets.join('、または')}です。`,
  },
  ko: {
    number: koreanNumber,
    count: koreanNumber,
    form: () => 0,
    unit: { usd: ['미국 달러'], htg: ['구르드'], htd: ['아이티 달러'], eur: ['유로'], cad: ['캐나다 달러'], dop: ['도미니카 페소'] },
    cents: { usd: ['센트'], htg: ['상팀'], htd: ['센트'], eur: ['센트'], cad: ['센트'], dop: ['센타보'] },
    centsAlone: { usd: ['미국 센트'], htg: ['상팀'], htd: ['아이티 센트'], eur: ['유로센트'], cad: ['캐나다 센트'], dop: ['도미니카 센타보'] },
    amount: (count, noun) => `${count} ${noun}`,
    withCents: (whole, cents) => `${whole} ${cents}`,
      sentence: (source, targets) => `${source}${koreanTopic(source)} ${targets.join(', 또는 ')}입니다.`,
  },
};

/** The languages Kòb pa m can listen and speak in — Palefò's eleven. */
export const VOICE_LANGUAGES = Object.keys(LANGUAGES);

/** A whole number in words: 1500 → "mil senk san" / "one thousand five hundred" / "一千五百". */
export const numberWords = (n, lang = 'ht') => LANGUAGES[lang].number(Math.floor(n));

/**
 * An amount as it is said aloud, exact to the cent: "sèt dola ameriken ak
 * swasannkat santim". Nothing is rounded away or called approximate — the voice
 * says the figure the screen shows. Null when the amount is too large to say.
 */
export function moneyWords(amount, currency, lang = 'ht') {
  const L = LANGUAGES[lang];
  const totalCents = Math.round(amount * 100);
  if (!L?.unit[currency] || !(totalCents >= 0) || totalCents >= MAX_SPOKEN * 100) return null;
  const whole = Math.floor(totalCents / 100), cents = totalCents % 100;
  const named = (n, forms, of) => L.amount(L.count(n, of), forms[Math.min(L.form(n), forms.length - 1)]);
  if (!whole && cents) return named(cents, L.centsAlone[currency]);
  const text = named(whole, L.unit[currency], currency);
  return cents ? L.withCents(text, named(cents, L.cents[currency])) : text;
}

/**
 * "Senk san dola ameriken fè swasannsenk mil goud, oswa trèz mil dola ayisyen."
 * `source` and each of `targets` are { amount, currency }. Null when any of
 * them cannot be said.
 */
export function conversionSentence(source, targets, lang = 'ht') {
  const L = LANGUAGES[lang];
  if (!L || !targets?.length) return null;
  const parts = [source, ...targets].map((m) => moneyWords(m?.amount, m?.currency, lang));
  if (parts.some((p) => !p)) return null;
  const sentence = L.sentence(parts[0], parts.slice(1), source.amount);
  return sentence[0].toUpperCase() + sentence.slice(1);
}

/**
 * "To referans la se san trant goud ak senkanntwa santim pou yon dola ameriken."
 * — the BRH's reference rate, in Kreyòl: in words for the voice, or with the
 * figure (`figure: true`) for the screen. Null when `rate` is not a rate.
 */
export function rateSentence(rate, { figure = false } = {}) {
  const totalCents = Math.round(rate * 100);
  if (!(totalCents > 0) || totalCents >= MAX_SPOKEN * 100) return null;
  const whole = Math.floor(totalCents / 100), cents = totalCents % 100;
  const goud = figure ? `${rate.toFixed(2)} goud`
    : `${numberWords(whole)} goud${cents ? ` ak ${numberWords(cents)} santim` : ''}`;
  return `To referans la se ${goud} pou yon dola ameriken.`;
}

// "The foreign currency is now the euro." — said when the currency was changed by voice.
const startsWithVowel = (w) => /^[aeiouáéíóúàèìòùâêîôû]/i.test(w);
const SWITCHED = {
  ht: (u) => `Kounye a, lajan etranje a se ${u}.`,
  en: (u) => `The foreign currency is now the ${u}.`,
  fr: (u) => `La devise étrangère est maintenant ${startsWithVowel(u) ? "l'" : 'le '}${u}.`,
  es: (u) => `La moneda extranjera ahora es el ${u}.`,
  pt: (u) => `A moeda estrangeira agora é o ${u}.`,
  it: (u) => `La valuta estera ora è ${startsWithVowel(u) ? "l'" : 'il '}${u}.`,
  de: (u) => `Die Fremdwährung ist jetzt der ${u.replace(/^(\w+)er /, '$1e ')}.`,   // der kanadische Dollar
  ru: (u) => `Теперь иностранная валюта — ${u}.`,
  zh: (u) => `现在的外币是${u}。`,
  ja: (u) => `外貨は${u}になりました。`,
  ko: (u) => `이제 외화는 ${u}입니다.`,
};

/** "Kounye a, lajan etranje a se ewo." Null for a currency or language it cannot name. */
export function switchedSentence(currency, lang = 'ht') {
  const unit = LANGUAGES[lang]?.unit[currency]?.[0];
  return unit && ['usd', 'eur', 'cad', 'dop'].includes(currency) ? SWITCHED[lang](unit) : null;
}
